import React, {useCallback, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import createAxiosInstance from "../support/AxiosSupport";

/** Ответ `GET /autopilot/status` (backend `AutopilotStatusDto`). */
export interface AutopilotStatus {
    /** Глобальное расписание фоновых запусков (серверная конфигурация). */
    schedulerEnabled: boolean;
    /** Персональный тумблер «Фоновый автопилот» владельца. */
    backgroundTaskEnabled: boolean;
    autopilotLevel: string | null;
    autopilotLevelLabel: string;
    /** true — система сама применяет обратимые решения. */
    autoApplyEnabled: boolean;
    nextRunAt: string | null;
    /** Согласованная строка статуса, собранная бэкендом из всех трёх переключателей. */
    statusText: string;
}

interface Props {
    ownerId: string | null;
    /** Тумблер фоновой задачи доступен только в собственном гардеробе. */
    canEdit: boolean;
}

const STATUS_URL = '/autopilot/status';
const BACKGROUND_TASKS_URL = '/profile/settings/background-tasks';
const BACKGROUND_TASKS_ROUTE = '/settings/profile/background-tasks';

/** Настройки фоновых задач: PUT принимает полное состояние всех тумблеров. */
interface BackgroundTaskSettings {
    aiAnalysisEnabled: boolean;
    analyticsEnabled: boolean;
    autopilotEnabled: boolean;
    disposalEnabled: boolean;
}

const formatNextRun = (iso: string | null): string | null => {
    if (!iso) {
        return null;
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return null;
    }
    return date.toLocaleString('ru-RU', {day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit'});
};

/**
 * Единственный статус-блок автопилота: три переключателя, которые раньше назывались одним
 * словом «автопилот», показаны раздельно и подписаны так, чтобы не противоречить друг другу
 * (было: «Фоновый автопилот: включён» рядом с «Автопилот выключен (уровень ASSIST)»).
 *
 * <p>Тумблер фоновой задачи — здесь же, на тот же эндпоинт, что и страница профиля
 * «Фоновые задачи»; остальные настройки правятся в профиле по ссылке.</p>
 */
const AutopilotStatusBlock: React.FC<Props> = ({ownerId, canEdit}) => {
    const [status, setStatus] = useState<AutopilotStatus | null>(null);
    const [failed, setFailed] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(false);

    const load = useCallback(async () => {
        setFailed(false);
        try {
            const api = await createAxiosInstance();
            const params = ownerId ? {ownerId} : {};
            const res = await api.get<AutopilotStatus>(STATUS_URL, {params});
            if (typeof res.data?.statusText === 'string' && typeof res.data?.schedulerEnabled === 'boolean') {
                setStatus(res.data);
            } else {
                setFailed(true);
            }
        } catch (e) {
            setFailed(true);
        }
    }, [ownerId]);

    useEffect(() => {
        load();
    }, [load]);

    /** Переключить фоновую задачу: PUT требует полного состояния, поэтому читаем его перед записью. */
    const toggleBackgroundTask = async () => {
        if (!status || saving) {
            return;
        }
        setSaving(true);
        setSaveError(false);
        try {
            const api = await createAxiosInstance();
            const current = await api.get<BackgroundTaskSettings>(BACKGROUND_TASKS_URL);
            await api.put(BACKGROUND_TASKS_URL, {
                ...current.data,
                autopilotEnabled: !status.backgroundTaskEnabled,
            });
            await load();
        } catch (e) {
            setSaveError(true);
        } finally {
            setSaving(false);
        }
    };

    if (failed || !status) {
        return null;
    }

    // Подпись режима берём с бэкенда; страхуемся от неполного ответа, чтобы блок не падал.
    const modeLabel = status.autopilotLevel
        ? (status.autoApplyEnabled ? 'автоприменение' : (status.autopilotLevelLabel || '').toLowerCase())
        : 'не выбран';
    const nextRun = formatNextRun(status.nextRunAt);

    return (
        <section
            data-testid="autopilot-status"
            className="rounded-xl border border-gray-200 dark:border-ink-700 p-4 space-y-3"
        >
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Автопилот</h2>

            <p className="text-sm text-gray-700 dark:text-gray-300" data-testid="autopilot-status-text">
                {status.statusText}
            </p>

            <dl className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                <div className="flex gap-1">
                    <dt>Расписание:</dt>
                    <dd data-testid="autopilot-status-scheduler">
                        {status.schedulerEnabled ? 'включено' : 'выключено'}
                    </dd>
                </div>
                <div className="flex gap-1">
                    <dt>Режим:</dt>
                    <dd data-testid="autopilot-status-mode">{modeLabel}</dd>
                </div>
                <div className="flex gap-1">
                    <dt>Фоновая задача:</dt>
                    <dd data-testid="autopilot-status-background">
                        {status.backgroundTaskEnabled ? 'вкл' : 'выкл'}
                    </dd>
                </div>
                {nextRun && (
                    <div className="flex gap-1">
                        <dt>Ближайший запуск:</dt>
                        <dd data-testid="autopilot-status-next-run">{nextRun}</dd>
                    </div>
                )}
            </dl>

            {canEdit && (
                <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            role="switch"
                            aria-checked={status.backgroundTaskEnabled}
                            aria-label="Фоновая задача автопилота"
                            disabled={saving}
                            onClick={toggleBackgroundTask}
                            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-60 ${
                                status.backgroundTaskEnabled ? 'bg-brand-600' : 'bg-gray-200 dark:bg-gray-600'
                            }`}
                        >
                            <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    status.backgroundTaskEnabled ? 'translate-x-6' : 'translate-x-1'
                                }`}
                            />
                        </button>
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                            Фоновая задача автопилота
                        </span>
                    </div>
                    <Link
                        to={BACKGROUND_TASKS_ROUTE}
                        className="text-sm text-primary-600 dark:text-primary-400 hover:underline min-h-[44px] flex items-center"
                    >
                        Все фоновые задачи в профиле
                    </Link>
                </div>
            )}

            {saveError && (
                <p className="text-sm text-rose-600 dark:text-rose-400" data-testid="autopilot-status-save-error">
                    Не удалось сохранить настройку. Попробуйте ещё раз.
                </p>
            )}
        </section>
    );
};

export default AutopilotStatusBlock;
